import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import {
  Box,
  Button,
  List,
  ListItem,
  Textarea,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { fetchWrapper } from '../../../helpers';

function Create({ meClose, loadList, rs, isOpenEdit }) {
  const [messageTypeList, setMessageTypeList] = useState([]);
  const [channelList, setChannelList] = useState([]);
  const [variables, setVariables] = useState([]);
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [messagesVariables, setMessagesVariables] = useState([]);
  const [showSearchBarMessage, setShowSearchBarMessage] = useState(false);
  const [showSearchBarPushNotification, setShowSearchBarPushNotification] = useState(false);
  const messageTextareaRef = useRef(null);
  const pushNotificationTextareaRef = useRef(null);

  // Expiry options with values in milliseconds
  const expiryOptions = [
    { value: 0, label: 'Never expiry' },
    { value: 30000, label: '30 seconds' },
    { value: 60000, label: '1 minute' },
    { value: 120000, label: '2 minutes' },
    { value: 180000, label: '3 minutes' },
    { value: 300000, label: '5 minutes' },
    { value: 600000, label: '10 minutes' },
    { value: 900000, label: '15 minutes' }
  ];

  const [initialValues, setInitialValues] = useState({
    name: '',
    message: '',
    short_message: '',
    message_template_id: '',
    channel_id: '',
    whatsapp_template_name: '',
    selected_variables: [],
    isEventTrackingEnabled: false,
    events: [],
    isExpiryEnabled: false,
    expiry_time: 0
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Template Name is required'),
    message: Yup.string().required('Message is required'),
    message_template_id: Yup.string().required('Template Type is required'),
    expiry_time: Yup.number().when('isExpiryEnabled', {
      is: true,
      then: (schema) => schema.min(0, 'Please select an expiry time').required('Expiry time is required when expiry is enabled'),
      otherwise: (schema) => schema
    })
  });

  useEffect(() => {
    loadMessageTypeList();
    loadChannelList();
    loadVariableList();
  }, []);

  const loadMessageTypeList = async () => {
    const url = `${process.env.REACT_APP_API_URL}/message-template-type/list`;
    const response = await fetchWrapper.get(url);
    setMessageTypeList({ data: response.data });
  };

  const loadChannelList = async () => {
    const url = `${process.env.REACT_APP_API_URL}/channel/list`;
    const response = await fetchWrapper.get(url);
    setChannelList(response);
  };

  const loadVariableList = async () => {
    const url = `${process.env.REACT_APP_API_URL}/variables/list`;
    const response = await fetchWrapper.get(url);
    setVariables(response.data);
  };

  // Memoized function to extract variables from message
  const extractVariablesFromMessage = useCallback((message) => {
    if (!message || !variables.length) return [];
    
    const variablePattern = /\$([a-zA-Z_][a-zA-Z0-9_]*)/g;
    const matches = [];
    let match;
    
    while ((match = variablePattern.exec(message)) !== null) {
      const variableName = `$${match[1]}`;
      // Find the variable in the variables list to get display_name
      const variableObj = variables.find(v => v.variable === variableName);
      if (variableObj && !matches.find(m => m.value === variableName)) {
        matches.push({
          value: variableName,
          label: variableObj.display_name
        });
      }
    }
    
    return matches;
  }, [variables]);

  // Update messagesVariables whenever message or variables change
  useEffect(() => {
    if (initialValues.message && variables.length > 0) {
      const extractedVariables = extractVariablesFromMessage(initialValues.message);
      setMessagesVariables(extractedVariables.map(v => ({
        variable: v.value,
        display_name: v.label
      })));
    }
  }, [initialValues.message, variables, extractVariablesFromMessage]);

  const filterVariables = (query) => {
    const filtered = variables.filter((variable) =>
      variable.variable.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredVariables(filtered);
  };

  const handleMessageChange = (e, setFieldValue) => {
    const value = e.target.value;
    setFieldValue('message', value);
  
    // Extract variables from the message - use timeout to ensure state is updated
    setTimeout(() => {
      const extractedVariables = extractVariablesFromMessage(value);
      const newMessagesVariables = extractedVariables.map(v => ({
        variable: v.value,
        display_name: v.label
      }));
      
      setMessagesVariables(newMessagesVariables);
  
      // If event tracking is enabled, update the first event's properties
      if (initialValues.isEventTrackingEnabled && extractedVariables.length > 0) {
        const currentEvents = initialValues.events || [];
        if (currentEvents.length > 0) {
          const updatedEvents = currentEvents.map((event, index) => {
            if (index === 0) {
              return { ...event, properties: extractedVariables };
            }
            return event;
          });
          setFieldValue('events', updatedEvents);
        }
      }
    }, 0);
  
    // Find the last `$` and what's typed after it for autocomplete
    const cursorPos = e.target.selectionStart;
    const uptoCursor = value.slice(0, cursorPos);
    const dollarIndex = uptoCursor.lastIndexOf('$');
  
    if (dollarIndex !== -1) {
      const searchQuery = uptoCursor.slice(dollarIndex + 1);
  
      // Only show if query is alphanumeric
      if (/^\w*$/.test(searchQuery)) {
        setShowSearchBarMessage(true);
        setShowSearchBarPushNotification(false);
        filterVariables(searchQuery);
        return;
      }
    }
  
    setShowSearchBarMessage(false);
  };

  const handlePushNotificationChange = (e, setFieldValue) => {
    const value = e.target.value;
    setFieldValue('short_message', value);

    if (value.includes('$')) {
      const searchQuery = value.split('$').pop();
      setShowSearchBarPushNotification(true);
      setShowSearchBarMessage(false);
      filterVariables(searchQuery);
    } else {
      setShowSearchBarPushNotification(false);
    }
  };

  const insertVariable = (variable, fieldName, setFieldValue, currentValue, textareaRef) => {
    const lastDollarIndex = currentValue.lastIndexOf('$');
    const updatedValue =
      lastDollarIndex >= 0
        ? `${currentValue.substring(0, lastDollarIndex)}${variable.variable} `
        : `${currentValue}${variable.variable} `;

    setFieldValue(fieldName, updatedValue);
    setShowSearchBarMessage(false);
    setShowSearchBarPushNotification(false);
    
    // Update messagesVariables immediately
    setMessagesVariables((prev) => {
      const exists = prev.find(v => v.variable === variable.variable);
      return exists ? prev : [...prev, variable];
    });
    
    if (textareaRef.current) {
      textareaRef.current.focus();
      const newCursorPos = lastDollarIndex + variable.variable.length + 1;
      setTimeout(() => {
        textareaRef.current.selectionStart = newCursorPos;
        textareaRef.current.selectionEnd = newCursorPos;
      }, 0);
    }
  };

  // Enhanced effect for handling edit mode with better state management
  useEffect(() => {
    if (isOpenEdit && rs && variables.length > 0) {
      // Process events if they exist
      let processedEvents = [];
      
      if (rs.events) {
        // Handle case where events might be a JSON string
        let eventsData = rs.events;
        if (typeof rs.events === 'string') {
          try {
            eventsData = JSON.parse(rs.events);
          } catch (e) {
            console.error('Error parsing events:', e);
            eventsData = {};
          }
        }
        
        // Convert events object to array format
        if (eventsData && typeof eventsData === 'object') {
          processedEvents = Object.entries(eventsData).map(([eventName, properties], index) => ({
            id: index + 1,
            event: eventName,
            properties: Array.isArray(properties) ? properties.map(prop => ({
              value: prop,
              label: variables.find(v => v.variable === prop)?.display_name || prop
            })) : []
          }));
        }
      }
      
      // If no events exist but we have a message, extract variables for potential event creation
      if (processedEvents.length === 0 && rs.message) {
        const extractedVariables = extractVariablesFromMessage(rs.message);
        if (extractedVariables.length > 0) {
          processedEvents = [{
            id: 1,
            event: '',
            properties: []
          }];
        }
      }
      
      // Ensure at least one empty event exists
      if (processedEvents.length === 0) {
        processedEvents = [{
          id: 1,
          event: '',
          properties: []
        }];
      }

      const newInitialValues = {
        name: rs.name || '',
        message: rs.message || '',
        message_template_id: rs.message_template_id || '',
        channel_id: rs.channel_id || '',
        whatsapp_template_name: rs.whatsapp_template_name || '',
        id: rs.id || '',
        short_message: rs.short_message || '',
        selected_variables: rs.selected_variables || [],
        isEventTrackingEnabled: rs.events ? true : false,
        events: processedEvents,
        isExpiryEnabled: rs.expiry_time !== undefined && rs.expiry_time !== null ? true : false,
        expiry_time: rs.expiry_time || 0
      };
      
      setInitialValues(newInitialValues);
      
      // Extract and set message variables
      if (rs.message) {
        const extractedVariables = extractVariablesFromMessage(rs.message);
        setMessagesVariables(extractedVariables.map(v => ({
          variable: v.value,
          display_name: v.label
        })));
      }
    }
  }, [isOpenEdit, rs, variables, extractVariablesFromMessage]);

  const onSubmit = async (values, { setSubmitting }) => {
    const processedMessage = values.message
      .split('\n')
      .filter((item) => item !== "")
      .join("\n")
      .replace(/\n/g, '\\n');

    const eventsObject = {};
    values.events.forEach((eventItem) => {
      if (eventItem.event && eventItem.event.trim() !== '') {
        eventsObject[eventItem.event] = eventItem.properties?.map((prop) =>
          typeof prop === 'object' ? prop.value : prop
        ) || [];
      }
    });

    const payloadwithEvent = {
      ...values,
      message: processedMessage,
      events: eventsObject,
      expiry_time: values.isExpiryEnabled ? values.expiry_time : null
    };
    
    const payloadwithoutEvent = {
      name: values.name,
      message: processedMessage,
      short_message: values.short_message,
      message_template_id: values.message_template_id,
      channel_id: values.channel_id,
      whatsapp_template_name: values.whatsapp_template_name,
      expiry_time: values.isExpiryEnabled ? values.expiry_time : null,
      id:rs?.id,
      events:null
    };
    
    let payload = values?.isEventTrackingEnabled ? payloadwithEvent : payloadwithoutEvent
    
    if (isOpenEdit) {
      try {
        const url = `${process.env.REACT_APP_API_URL}/template-message/update`;
        await fetchWrapper.post(url, payload)
        loadList()
        meClose();
        toast(`Record has been saved `, {
          type: "info"
        });
      } catch (error) {
        toast.error('Error updating record');
      }
    } else {
      try {
        const url = `${process.env.REACT_APP_API_URL}/template-message/create`;
        await fetchWrapper.post(url, payload);
        loadList();
        meClose();
        toast(`Record has been saved`, { type: 'info' });
      } catch (error) {
        toast.error('Error saving record');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const renderVariablesList = (onSelect) => (
    <Box mt={2} borderWidth="1px" borderRadius="md" overflow="hidden">
      <List spacing={1}>
        {filteredVariables.map((variable) => (
          <ListItem
            key={variable.id}
            padding={2}
            _hover={{ background: 'gray.100', cursor: 'pointer' }}
            onClick={() => onSelect(variable)}
          >
            {variable.variable} - {variable.display_name}
          </ListItem>
        ))}
        {filteredVariables.length === 0 && (
          <ListItem padding={2} textAlign="center">
            No variables found
          </ListItem>
        )}
      </List>
    </Box>
  );

  // Convert data for React Select
  const messageTypeOptions = messageTypeList?.data?.map((type) => ({
    value: type.id,
    label: type.name
  })) || [];

  const channelOptions = channelList?.data?.map((channel) => ({
    value: channel.id,
    label: channel.name
  })) || [];

  // Memoize messageVariables to prevent unnecessary re-renders
  const messageVariables = useMemo(() => {
    return messagesVariables?.map((item) => ({
      value: item.variable,
      label: item.display_name
    })) || [];
  }, [messagesVariables]);

  // Custom styles for React Select to match Chakra UI theme
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
      '&:hover': {
        borderColor: '#cbd5e0'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#3182ce'
        : state.isFocused
          ? '#e2e8f0'
          : 'white',
      color: state.isSelected ? 'white' : '#2d3748',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3182ce' : '#f7fafc'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e2e8f0',
      borderRadius: '16px',
      padding: '2px 8px',
      margin: '2px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#2d3748',
      fontSize: '14px'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#718096',
      borderRadius: '50%',
      '&:hover': {
        backgroundColor: '#cbd5e0',
        color: '#2d3748',
      },
    }),
  };

  // Expiry Component
  const ExpirySection = ({ values, setFieldValue, errors, touched }) => {
    const handleExpiryToggle = (checked) => {
      setFieldValue('isExpiryEnabled', checked);
      if (!checked) {
        setFieldValue('expiry_time', 0);
      }
    };

    return (
      <div style={{ marginTop: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '10px',
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            marginRight: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: "#F5F5F5"
          }}
            onClick={() => handleExpiryToggle(!values.isExpiryEnabled)}
          >
            {values.isExpiryEnabled && (
              <span style={{ color: 'green', fontSize: '12px' }}>✓</span>
            )}
          </div>
          <span style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#333'
          }}>
            Enable Expiry
          </span>
        </div>

        {values.isExpiryEnabled && (
          <FormControl isInvalid={errors.expiry_time && touched.expiry_time} style={{ marginTop: "10px" }}>
            <FormLabel>Expiry Time</FormLabel>
            <Select
              name="expiry_time"
              value={expiryOptions.find(option => option.value === values.expiry_time)}
              onChange={(selectedOption) => setFieldValue('expiry_time', selectedOption?.value || 0)}
              options={expiryOptions}
              placeholder="Select expiry time"
              styles={customSelectStyles}
              isClearable={false}
            />
            <FormErrorMessage>{errors.expiry_time}</FormErrorMessage>
          </FormControl>
        )}
      </div>
    );
  };

  // Events Tracking Component with improved state management
  const EventsTracking = ({ messageVariables, values, setFieldValue }) => {
    const [events, setEvents] = useState(values.events.length > 0 ? values.events : [
      {
        id: 1,
        event: '',
        properties: []
      }
    ]);

    // Update local events state when values.events changes
    useEffect(() => {
      if (values.events.length > 0) {
        setEvents(values.events);
      }
    }, [values.events]);

    const addMoreEvent = () => {
      const newEvent = {
        id: Date.now(),
        event: '',
        properties: []
      };
      const updatedEvents = [...events, newEvent];
      setEvents(updatedEvents);
      setFieldValue('events', updatedEvents);
    };

    const removeEvent = (eventId) => {
      if (events.length > 1) {
        const updatedEvents = events.filter(event => event.id !== eventId);
        setEvents(updatedEvents);
        setFieldValue('events', updatedEvents);
      }
    };

    const updateEvent = (eventId, field, value) => {
      const updatedEvents = events.map(event =>
        event.id === eventId
          ? { ...event, [field]: value }
          : event
      );
      setEvents(updatedEvents);
      setFieldValue('events', updatedEvents);
    };

    const handleEventTrackingChange = (checked) => {
      setFieldValue('isEventTrackingEnabled', checked);
      if (checked) {
        // Extract variables from current message
        const extractedVariables = extractVariablesFromMessage(values.message);
        
        if (extractedVariables.length > 0) {
          // Pre-fill ONLY the first event with extracted variables
          const updatedEvents = events.map((event, index) => {
            if (index === 0) {
              return { ...event, properties: extractedVariables };
            }
            return event;
          });
          setEvents(updatedEvents);
          setFieldValue('events', updatedEvents);
        }
      }
    };

    return (
      <div style={{ marginTop: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '10px',
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            marginRight: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: "#F5F5F5"
          }}
            onClick={() => handleEventTrackingChange(!values.isEventTrackingEnabled)}
          >
            {values.isEventTrackingEnabled && (
              <span style={{ color: 'green', fontSize: '12px' }}>✓</span>
            )}
          </div>
          <span style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#333'
          }}>
            Events Tracking
          </span>
        </div>

        {values.isEventTrackingEnabled && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                marginBottom: '10px',
                fontWeight: '500',
                color: '#666'
              }}>
                <div style={{ width: '200px', marginRight: '20px' }}>Event</div>
                <div style={{ flex: 1 }}>Properties</div>
              </div>

              {events.map((event, index) => (
                <div key={event.id} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: '15px',
                  padding: '15px',
                  borderRadius: '8px',
                }}>
                  <div style={{ width: '200px', marginRight: '20px' }}>
                    <Input
                      value={event.event}
                      onChange={(e) => updateEvent(event.id, 'event', e.target.value)}
                      placeholder="Enter event name"
                      isRequired={values.isEventTrackingEnabled}
                    />
                  </div>

                  <div style={{ flex: 1, marginRight: '10px' }}>
                    <Select
                      value={event.properties}
                      onChange={(selectedOptions) =>
                        updateEvent(event.id, 'properties', selectedOptions || [])
                      }
                      options={messageVariables}
                      isMulti
                      placeholder="Select properties"
                      styles={customSelectStyles}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                    />
                  </div>

                  {events.length > 1 && (
                    <Button
                      onClick={() => removeEvent(event.id)}
                      size="sm"
                      colorScheme="red"
                      variant="solid"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              onClick={addMoreEvent}
              variant="ghost"
              color="#007bff"
              size="sm"
            >
              Add More
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({
        handleSubmit,
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleBlur,
      }) => (
        <form onSubmit={handleSubmit}>
          <FormControl isInvalid={errors.name && touched.name} style={{ marginTop: "10px" }}>
            <FormLabel>Template Name <span style={{ color: "red" }}>*</span></FormLabel>
            <Input
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder='Please Enter Template Name'
            />
            <FormErrorMessage>{errors.name}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={errors.message && touched.message} style={{ marginTop: "10px" }}>
            <FormLabel>Message <span style={{ color: "red" }}>*</span></FormLabel>
            <Textarea
              ref={messageTextareaRef}
              name="message"
              value={values.message}
              onChange={(e) => handleMessageChange(e, setFieldValue)}
              onBlur={handleBlur}
              placeholder="Type your message... Use $ to insert variables"
              minH="120px"
              resize="vertical"
            />
            <FormErrorMessage>{errors.message}</FormErrorMessage>
            {showSearchBarMessage && renderVariablesList((variable) =>
              insertVariable(variable, 'message', setFieldValue, values.message, messageTextareaRef)
            )}
          </FormControl>

          <FormControl isInvalid={errors.message_template_id && touched.message_template_id} style={{ marginTop: "10px" }}>
            <FormLabel>Template Type Name <span style={{ color: "red" }}>*</span></FormLabel>
            <Select
              name="message_template_id"
              value={messageTypeOptions.find(option => option.value === values.message_template_id)}
              onChange={(selectedOption) => setFieldValue('message_template_id', selectedOption?.value || '')}
              onBlur={handleBlur}
              options={messageTypeOptions}
              placeholder="Select template type"
              styles={customSelectStyles}
              isClearable
            />
            <FormErrorMessage>{errors.message_template_id}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={errors.channel_id && touched.channel_id} style={{ marginTop: "10px" }}>
            <FormLabel>Channel</FormLabel>
            <Select
              name="channel_id"
              value={channelOptions.find(option => option.value === values.channel_id)}
              onChange={(selectedOption) => setFieldValue('channel_id', selectedOption?.value || '')}
              onBlur={handleBlur}
              options={channelOptions}
              placeholder="Select channel"
              styles={customSelectStyles}
              isClearable
            />
            <FormErrorMessage>{errors.channel_id}</FormErrorMessage>
          </FormControl>

          <FormControl style={{ marginTop: "10px" }}>
            <FormLabel>WhatsApp Template Name</FormLabel>
            <Input
              name="whatsapp_template_name"
              value={values.whatsapp_template_name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder='Please Enter Template Name'
            />
          </FormControl>

          <FormControl style={{ marginTop: "10px" }}>
            <FormLabel>Push Notification Message</FormLabel>
            <Textarea
              ref={pushNotificationTextareaRef}
              name="short_message"
              value={values.short_message}
              onChange={(e) => handlePushNotificationChange(e, setFieldValue)}
              onBlur={handleBlur}
              placeholder="Type your push notification message... Use $ to insert variables"
              minH="120px"
              resize="vertical"
            />
          </FormControl>

          {/* Expiry Section */}
          <ExpirySection
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
          />

          {/* Events Tracking Section */}
          <EventsTracking
            messageVariables={messageVariables}
            values={values}
            setFieldValue={setFieldValue}
          />

          <Button
            type="submit"
            py="10px"
            px="25px"
            color="#fff"
            bg="#2563eb"
            fontSize="14px"
            fontWeight="500"
            fontFamily="'Open Sans', sans-serif"
            mt={5}
            mb={5}
          >
            Add New Message Template
          </Button>
        </form>
      )}
    </Formik>
  );
}

export default Create;